import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FullModal from '../components/FullModal';
import PageTitle from '../components/PageTitle';
import Loader from '../components/Loader';

const DeleteAccountConfirm = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [fetchStatus, setFetchStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [pendingDeletionAt, setPendingDeletionAt] = useState(null);
    const hasCalled = useRef(false);

    const sendConfirmation = async () => {
        setFetchStatus('loading');
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/users/confirm-account-deletion`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token })
            });
            const result = await response.json();
            if (response.ok) {
                setPendingDeletionAt(result.payload?.pendingDeletionAt);
                setFetchStatus('succeeded');
            } else {
                setError(result.message || t('AccountDeletion.confirmPage.invalidToken'));
                setFetchStatus('failed');
            }
        } catch (err) {
            setError(err.message || t('AccountDeletion.confirmPage.connectionError'));
            setFetchStatus('failed');
        }
    };

    useEffect(() => {
        if (hasCalled.current) return;
        hasCalled.current = true;
        if (!token) {
            setError(t('AccountDeletion.confirmPage.missingToken'));
            setFetchStatus('failed');
            return;
        }
        sendConfirmation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <FullModal px="4" items="center">
            <PageTitle backLink="/" title={t('AccountDeletion.confirmPage.title')} />

            <div className="flex flex-col items-center gap-4 max-w-xl w-full text-center my-8">
                {fetchStatus === 'loading' && <Loader />}

                {fetchStatus === 'succeeded' && pendingDeletionAt && (
                    <>
                        <h3 className="text-[#225424]">{t('AccountDeletion.confirmPage.successTitle')}</h3>
                        <p>
                            {t('AccountDeletion.confirmPage.successText', {
                                date: new Date(pendingDeletionAt).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })
                            })}
                        </p>
                        <p className="text-sm text-neutral-600">
                            {t('AccountDeletion.confirmPage.successHint')}
                        </p>
                        <Link to="/" className="primary mt-4">{t('AccountDeletion.confirmPage.backHome')}</Link>
                    </>
                )}

                {fetchStatus === 'failed' && (
                    <>
                        <h3 className="text-red-700">{t('AccountDeletion.confirmPage.errorTitle')}</h3>
                        <p className="text-sm">{error}</p>
                        <p className="text-xs text-neutral-600">
                            {t('AccountDeletion.confirmPage.errorHint')}
                        </p>
                        <Link to="/" className="primary mt-4">{t('AccountDeletion.confirmPage.backHome')}</Link>
                    </>
                )}
            </div>
        </FullModal>
    );
};

export default DeleteAccountConfirm;
